import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { ticketsAPI } from '../api/client';
import type { TicketPerson } from '../types';

interface TicketPersonPickerProps {
  value: TicketPerson | null;
  onChange: (person: TicketPerson | null) => void;
  placeholder?: string;
}

export default function TicketPersonPicker({ value, onChange, placeholder }: TicketPersonPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TicketPerson[]>([]);

  useEffect(() => {
    const text = query.trim();
    if (value || text.length < 2) return;
    const timer = window.setTimeout(() => {
      ticketsAPI.people(text).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query, value]);

  const shown = query.trim().length < 2 ? [] : results;

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-navy/30 bg-navy/5 px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-navy truncate">{value.name}</p>
          <p className="text-xs text-gray-500 truncate">{value.email}</p>
        </div>
        <button
          type="button"
          onClick={() => { onChange(null); setQuery(''); }}
          className="p-1.5 rounded-lg hover:bg-white transition shrink-0"
          aria-label="Choose someone else"
        >
          <X size={16} className="text-gray-500" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder || 'Search by name or email'}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
        />
      </div>
      {shown.length > 0 && (
        <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
          {shown.map((person) => (
            <button
              key={person.id}
              type="button"
              onClick={() => onChange(person)}
              className="w-full text-left px-3 py-2 hover:bg-off-white transition"
            >
              <p className="text-sm font-medium text-navy truncate">{person.name}</p>
              <p className="text-xs text-gray-400 truncate">{person.email}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
